import { createContext, useContext } from 'react';

// En el link público el reporte responde al ancho de la pantalla. En la vista
// previa "Celular" se fuerza el diseño angosto, porque las clases md:* miran
// la pantalla del comercial y no el ancho del marco. El PDF usa siempre el
// diseño ancho, sin importar el tamaño de la ventana.
export type ReportLayout = 'responsive' | 'mobile' | 'pdf';

// Paleta sobria del documento: arribado en tinta, en tránsito en gris claro.
// El rojo queda solo para lo vencido.
export const ARRIVED_CLASS = 'bg-default-800';
export const IN_TRANSIT_CLASS = 'bg-default-300';
// Cargado y sin zarpar: solo en el plan comercial.
export const NOT_DEPARTED_CLASS =
  'border border-default-400 bg-background [background-image:repeating-linear-gradient(135deg,hsl(var(--nextui-default-400))_0_1px,transparent_1px_5px)]';

export const TRANSPORT_LABELS = {
  A: 'aéreo',
  M: 'marítimo',
  T: 'terrestre',
} as const;

// Nombres que vienen en mayúsculas (puertos, variedades) en formato título:
// "SHANGHAI PUDONG" → "Shanghai Pudong".
export const titleCase = (value: string) =>
  value
    .toLowerCase()
    .replace(
      /(^|[\s/(-])(\p{L})/gu,
      (_, sep: string, letter: string) => sep + letter.toUpperCase()
    );

export const PLAN_UNIT_LABELS = {
  kg: 'kg',
  ceq: 'cajas eq.',
  shipment: 'embarques',
} as const;

// Embarques con decimales solo si el plan los tiene.
export const formatPlanAmount = (
  value: number,
  unit: keyof typeof PLAN_UNIT_LABELS,
  number: (value: number) => string
) =>
  unit === 'shipment' && !Number.isInteger(value)
    ? value.toFixed(1)
    : number(value);

export const ReportLayoutContext = createContext<ReportLayout>('responsive');

export const useReportLayout = () => {
  const layout = useContext(ReportLayoutContext);
  if (layout === 'pdf') {
    return {
      kpiGrid: 'grid-cols-4',
      sheetPadding: 'p-10',
      // La captura del PDF dibuja el texto un poco más ancho que la pantalla:
      // los títulos largos se partían y dejaban "KG" solo sobre la línea.
      panelTitle: 'whitespace-nowrap',
      // El texto pegado al borde derecho (datos al lado del título) se cortaba
      // en chino: la captura lo dibuja más ancho de lo que se midió.
      headerSlack: 'pr-3',
      // Tablas siempre como tabla; null = sin versión apilada.
      tableClass: '',
      stackClass: null,
      mobileHiddenClass: '',
      cellGap: 'pr-4',
      mobileOnlyClass: 'hidden',
    };
  }
  const isMobile = layout === 'mobile';
  return {
    kpiGrid: isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4',
    // Margen de la hoja: en la vista previa "Celular" no puede depender del
    // ancho de la pantalla del comercial.
    sheetPadding: isMobile ? 'p-5' : 'p-5 sm:p-10',
    panelTitle: '',
    headerSlack: '',
    // En celular las tablas se apilan por fila (ReportTable).
    tableClass: isMobile ? 'hidden' : 'hidden md:block',
    stackClass: isMobile ? '' : 'md:hidden',
    mobileHiddenClass: isMobile ? 'hidden' : 'hidden md:table-cell',
    // Espacio entre columnas: en celular más justo para que entren las cifras.
    cellGap: isMobile ? 'pr-2' : 'pr-2 md:pr-4',
    // Lo que en celular reemplaza a una columna mobileHidden.
    mobileOnlyClass: isMobile ? '' : 'md:hidden',
  };
};
